/**
 * What a plan costs THE PERSON LOOKING AT IT.
 *
 * Someone already on a plan does not pay the sticker price to move to a dearer
 * one — they pay the difference, because the plan they hold is credited against
 * it. Quoting the full amount to them overstates the cost of upgrading, often
 * by most of it: ¥300 when the answer is ¥200.
 *
 * ONE RULE, USED EVERYWHERE a plan's price appears to a signed-in customer —
 * the booking option, the checkout, the confirmation, the shop. Three screens
 * doing this arithmetic separately is how one of them ends up disagreeing with
 * what the card is actually charged.
 *
 * ⚠️ THE FIGURE IS INDICATIVE, NOT THE INVOICE. Stripe prorates by the time
 * left in the current period, so the real charge is this difference scaled to
 * the unused portion — never MORE than shown, and usually less. Say "about"
 * where the wording allows, and never present it as an exact total.
 */

export interface PlanCharge {
  /** What to show. The difference when upgrading, otherwise the full price. */
  amount: number;
  /** True when this is a top-up on a plan they already hold. */
  isUpgrade: boolean;
}

/**
 * @param planPrice     the plan's own monthly price
 * @param currentPrice  the monthly price of the plan they hold now, or null
 */
export function planChargeForViewer(
  planPrice: number | null | undefined,
  currentPrice: number | null | undefined
): PlanCharge | null {
  const price = Number(planPrice ?? NaN);
  if (!Number.isFinite(price)) return null;

  const current = Number(currentPrice ?? NaN);

  // No plan held, or the new one is not dearer: there is no difference to
  // quote. A DOWNGRADE deliberately shows its own full price rather than a
  // negative — "−¥200" reads as money coming back, and it does not.
  if (!Number.isFinite(current) || price <= current) {
    return { amount: price, isUpgrade: false };
  }

  return { amount: price - current, isUpgrade: true };
}
